import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { paymentsAPI, studentsAPI, batchesAPI, uploadAPI } from '../utils/api';
import { useStudentAlert } from '../hooks/useStudentAlert';
import StudentAlertModal from '../components/common/StudentAlertModal';
import OrganizationPaymentDetailsWidget from '../components/OrganizationPaymentDetailsWidget';
import { useTranslation } from 'react-i18next';
import { tField } from '../utils/bilingualHelper';

// Build month list STRICTLY from batch startDate → endDate.
// Falls back to just the current month if the batch has no dates set.
function getBatchMonths(batch) {
  const CURRENT = new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  if (!batch) return [CURRENT];

  const hasStart = batch.startDate;
  const hasEnd   = batch.endDate;

  // If the batch has no date info at all, show only the current month
  if (!hasStart && !hasEnd) return [CURRENT];

  let startD = hasStart ? new Date(batch.startDate) : new Date();
  let endD   = hasEnd   ? new Date(batch.endDate)   : (() => { const d = new Date(startD); d.setFullYear(d.getFullYear() + 1); return d; })();

  const list = [];
  let d = new Date(startD);
  d.setDate(1);
  let count = 0;
  while (d <= endD && count < 36) {
    list.push(d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }));
    d.setMonth(d.getMonth() + 1);
    count++;
  }
  return list.length > 0 ? list : [CURRENT];
}

const CURRENT_MONTH = new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

const isSameMonth = (m1, m2) => {
  if (!m1 || !m2) return false;
  const [mon1, y1] = m1.trim().split(' ');
  const [mon2, y2] = m2.trim().split(' ');
  if (!mon1 || !mon2 || !y1 || !y2) return m1 === m2;
  return mon1.substring(0, 3).toLowerCase() === mon2.substring(0, 3).toLowerCase() && y1 === y2;
};

// 2-Stage Payment Allocation Helper to isolate payments per batch
const calculateBatchPaidAmount = (student, targetBatch, monthStr, paymentsList, onlyApproved = true) => {
  const targetBatchId = targetBatch?._id || targetBatch?.id;
  const monthPayments = paymentsList.filter(p =>
    (p.student?._id === student._id || p.student === student._id || p.studentId === student.studentId) &&
    isSameMonth(p.month, monthStr) &&
    (onlyApproved ? p.verificationStatus === 'Approved' : (p.verificationStatus === 'Approved' || p.verificationStatus === 'Pending'))
  );

  // 1. Direct payments matching targetBatchId
  const directPayments = monthPayments.filter(p => {
    const pBatchId = p.batch?._id || p.batch || p.batchId;
    return targetBatchId && String(pBatchId) === String(targetBatchId);
  });

  // 2. Unassigned payments (legacy payments with no batch ID)
  const unassignedPayments = monthPayments.filter(p => {
    const pBatchId = p.batch?._id || p.batch || p.batchId;
    return !pBatchId;
  });

  const expectedFee = targetBatch?.feeAmount || student?.feeAmount || 1800;
  const directAmount = directPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  if (directAmount >= expectedFee) {
    return { allocatedPaid: expectedFee, expectedFee };
  }

  // Handle unassigned pool
  let unassignedPool = unassignedPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const stuBatches = student.batchIds?.length > 0 ? student.batchIds : (student.batch ? [student.batch] : [{ _id: 'unassigned', feeAmount: student.feeAmount || 1800 }]);

  for (const b of stuBatches) {
    const bId = b._id || b.id;
    if (String(bId) === String(targetBatchId)) break;
    const bFee = b.feeAmount || student.feeAmount || 1800;
    unassignedPool = Math.max(0, unassignedPool - bFee);
  }

  const needed = expectedFee - directAmount;
  const allocatedFromUnassigned = Math.min(needed, unassignedPool);
  return { allocatedPaid: directAmount + allocatedFromUnassigned, expectedFee };
};

export default function FinancialAuditor() {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(CURRENT_MONTH);
  const [loading, setLoading] = useState(true);

  const alertContext = useStudentAlert();

  // Global month list — only valid months for the selected batch
  const months = useMemo(() => {
    if (!selectedBatch) return [CURRENT_MONTH];
    const b = batches.find(x => x._id === selectedBatch);
    return getBatchMonths(b);
  }, [selectedBatch, batches]);

  // When batch filter changes, reset selected month if needed
  useEffect(() => {
    if (!months.includes(selectedMonth)) setSelectedMonth(months[0] ?? CURRENT_MONTH);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months]);

  // ─── Record Modal state ────────────────────────────────────────────────────
  const emptyForm = {
    studentId: '',
    paymentMethod: 'Cash',
    guardianName: 'Admin Entry',
    _parentName: '',
    _batchMonths: [],       // all valid months for this student's batch
    _feePerMonth: 1800,     // auto-detected from batch
    selectedMonths: [],     // user selection
    _paidMonths: [],        // months already paid (Approved) — read-only highlights
    _batchName: '',
    _batchId: '',
    receiptFile: null,      // for Card or Bank/QR Transfer
  };
  const [recordModal, setRecordModal] = useState(false);
  const [recordForm, setRecordForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchIdRef = React.useRef(0);

  useEffect(() => {
    let isMounted = true;
    fetchData(isMounted);
    return () => { isMounted = false; };
  }, []);

  const fetchData = async (isMounted = true) => {
    const currentId = ++fetchIdRef.current;
    try {
      const [stuRes, payRes, batchRes] = await Promise.all([
        studentsAPI.getAll(),
        paymentsAPI.getAll(),
        batchesAPI.getAll()
      ]);
      if (!isMounted || currentId !== fetchIdRef.current) return;
      setStudents(stuRes.data);
      setPayments(payRes.data);
      setBatches(batchRes.data);
    } catch (err) {
      if (!isMounted || currentId !== fetchIdRef.current) return;
      console.error(err);
    } finally {
      if (isMounted && currentId === fetchIdRef.current) setLoading(false);
    }
  };

  // Open modal pre-filled with student context
  const openModal = (student = null, batch = null, defaultMonth = null) => {
    const batchMonths = batch ? getBatchMonths(batch) : getBatchMonths(null);
    const feePerMonth = batch?.feeAmount ?? student?.feeAmount ?? 1800;

    // Find months already paid (Approved/Pending) for this student using the exact allocation logic
    const paidMonths = [];
    if (student) {
      batchMonths.forEach(m => {
        const { allocatedPaid } = calculateBatchPaidAmount(student, batch, m, payments, false);
        if (allocatedPaid >= feePerMonth) {
          paidMonths.push(m);
        }
      });
    }

    // Pre-select defaultMonth only if it hasn't been paid yet
    const preSelected = defaultMonth && !paidMonths.includes(defaultMonth)
      ? [defaultMonth]
      : [];

    setRecordForm({
      studentId:      student?.studentId ?? '',
      paymentMethod:  'Cash',
      guardianName:   student?.parentId?.name ? tField(student.parentId.name) : 'Admin Entry',
      _parentName:    student?.parentId?.name ? tField(student.parentId.name) : '',
      _batchMonths:   batchMonths,
      _feePerMonth:   feePerMonth,
      selectedMonths: preSelected,
      _paidMonths:    paidMonths,
      _batchName:     tField(batch?.name),
      _batchId:       batch?._id || batch?.id || '',
    });
    setRecordModal(true);
  };

  const toggleMonth = (month) => {
    setRecordForm(prev => {
      // Don't allow toggling already-paid months
      if (prev._paidMonths.includes(month)) return prev;
      return {
        ...prev,
        selectedMonths: prev.selectedMonths.includes(month)
          ? prev.selectedMonths.filter(m => m !== month)
          : [...prev.selectedMonths, month]
      };
    });
  };

  const totalAmount = recordForm._feePerMonth * recordForm.selectedMonths.length;

  const handleRecordSubmit = async () => {
    if (!recordForm.studentId) return showToast('Student ID is required.', 'error');
    if (recordForm.selectedMonths.length === 0) return showToast('Please select at least one month.', 'error');
    if (recordForm.paymentMethod === 'Bank/QR Transfer' && !recordForm.receiptFile) {
      return showToast(`Please upload a payment receipt for ${recordForm.paymentMethod} payments.`, 'error');
    }

    setSubmitting(true);
    try {
      let receiptUrl = null;
      if (recordForm.receiptFile) {
        const formData = new FormData();
        formData.append('receipt', recordForm.receiptFile);
        const uploadRes = await uploadAPI.paymentReceipt(formData);
        receiptUrl = uploadRes.data.url;
      }

      await paymentsAPI.submitMultiple({
        studentId:      recordForm.studentId,
        amountPerMonth: recordForm._feePerMonth,
        months:         recordForm.selectedMonths,
        paymentMethod:  recordForm.paymentMethod === 'Bank/QR Transfer' ? 'QR/Transfer' : recordForm.paymentMethod,
        paymentType:    'Monthly Fee',
        paidBy:         'Parent/Guardian',
        guardianName:   recordForm.guardianName,
        batchId:        recordForm._batchId,
        receiptUrl:     receiptUrl,
      });
      setRecordModal(false);
      setRecordForm(emptyForm);
      showToast(`Successfully recorded payment of HK$ ${totalAmount.toLocaleString()}!`, 'success');
      await fetchData();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to record payment', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title={t('financialAuditor.title')}>
      {/* 🚀 Beautiful Toast Notifications */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: toast.type === 'error' ? '#c0392b' : '#2d7a52',
          color: '#fff',
          padding: '16px 28px',
          borderRadius: 12,
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          zIndex: 1100,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          animation: 'fadeIn 0.2s ease'
        }}>
          <span>{toast.type === 'error' ? '⚠️' : '✅'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .fa-desktop-table { display: none; }
          .fa-page-header { flex-direction: column; align-items: flex-start; gap: 16px; }
          .fa-filter-row { width: 100%; display: flex; flex-wrap: wrap; gap: 8px; }
          .fa-student-card { background: white; border-radius: 12px; padding: 16px; margin-bottom: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
        }
        @media (min-width: 769px) {
          .fa-mobile-cards { display: none; }
          .fa-page-header { display: flex; justify-content: space-between; align-items: center; }
          .fa-filter-row { display: flex; gap: 12px; }
        }
      `}</style>
      <div className="fa-page-header">
        <div>
          <h1 className="dash-page-title">{t('financialAuditor.title', 'Monthly Fee Tracker')}</h1>
          <p className="text-sm text-muted">{t('financialAuditor.subtitle', 'Track and record student fee payments across all billing cycles.')}</p>
        </div>
        <div className="fa-filter-row">
          <select
            className="form-input-style"
            style={{ flex: 1, minWidth: 120, padding: '8px 16px', fontWeight: 700 }}
            value={selectedBatch ?? ''}
            onChange={e => setSelectedBatch(e.target.value || null)}
          >
            <option value="">Select Batch...</option>
            {batches.map(b => <option key={b._id} value={b._id}>{tField(b.name)}</option>)}
          </select>
          <select
            className="form-input-style"
            style={{ flex: 1, minWidth: 120, padding: '8px 16px', fontWeight: 700 }}
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
          >
            {months.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="dash-loading">⏳ Synchronizing financial ledger...</div>
      ) : (
        <div className="dash-card p-0" style={{ overflow: 'hidden', border: 'none', background: '#fff', boxShadow: '0 10px 30px rgba(123, 29, 58, 0.05)' }}>
          {!selectedBatch ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', gap: 16 }}>
              <span style={{ fontSize: 48 }}>🏫</span>
              <p style={{ fontSize: 16, color: '#888', fontWeight: 600, margin: 0 }}>{t('financialAuditor.emptySelectBatch', 'Please select a batch to view payments')}</p>
            </div>
          ) : (
          <>
          {/* ── Desktop Table ── */}
          <div className="fa-desktop-table" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: 'var(--black)', color: '#fff' }}>
                  <th style={{ padding: '20px 24px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5 }}>{t('financialAuditor.student')}</th>
                  <th style={{ padding: '20px 24px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5 }}>Enrollment</th>
                  <th style={{ padding: '20px 24px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5 }}>Monthly Fee</th>
                  <th style={{ padding: '20px 24px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5 }}>Status ({selectedMonth})</th>
                  <th style={{ padding: '20px 24px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const rows = [];
                  const filteredStudents = students.filter(s => {
                    const inCurrentBatch = s.batchIds?.some(b => (b._id || b.id || b) === selectedBatch) || (s.batch?._id || s.batch) === selectedBatch;
                    const inBatchHistory = s.batchHistory?.some(h => (h.batchId?._id || h.batchId)?.toString() === selectedBatch);
                    const inRemovedBatches = s.removedBatches?.some(b => (b.batchId?._id || b.batchId || b.batch)?.toString() === selectedBatch);
                    const hasPaymentInBatch = payments.some(p =>
                      (p.student?._id === s._id || p.student === s._id || p.studentId === s.studentId) &&
                      ((p.batch?._id || p.batch) === selectedBatch)
                    );
                    return inCurrentBatch || inBatchHistory || inRemovedBatches || hasPaymentInBatch;
                  });

                  filteredStudents.forEach(student => {
                    let stuBatches = student.batchIds?.length > 0 ? [...student.batchIds] : (student.batch ? [student.batch] : [{ _id: 'unassigned', name: student.currentLevel || 'Unassigned', feeAmount: student.feeAmount || 1800 }]);
                    
                    const inCurrent = stuBatches.some(b => (b._id || b.id) === selectedBatch);
                    if (!inCurrent && selectedBatch && selectedBatch !== 'All') {
                      const bDoc = batches.find(b => b._id === selectedBatch);
                      if (bDoc) {
                        stuBatches.push({ ...bDoc, isWithdrawn: true });
                      }
                    }

                    const relevantBatches = selectedBatch === 'All' ? stuBatches : stuBatches.filter(b => (b._id || b.id) === selectedBatch);

                    relevantBatches.forEach((batch, bIndex) => {
                      const { allocatedPaid, expectedFee } = calculateBatchPaidAmount(student, batch, selectedMonth, payments, true);

                      const isWithdrawnBatch = batch.isWithdrawn || student.status === 'Withdrawn';
                      let statusText = 'FEE DUE';
                      let statusColor = 'badge-red';
                      if (allocatedPaid >= expectedFee) { statusText = 'PAID IN FULL'; statusColor = 'badge-green'; }
                      else if (allocatedPaid > 0) { statusText = `PARTIAL (HK$ ${allocatedPaid.toLocaleString()})`; statusColor = 'badge-orange'; }
                      else if (isWithdrawnBatch) { statusText = 'WITHDRAWN'; statusColor = 'badge-grey'; }

                      rows.push({ student, batch, expectedFee, allocatedPaid, statusText, statusColor, isWithdrawnBatch, key: `${student._id}-${batch._id || bIndex}` });
                    });
                  });

                  return rows.map(({ student, batch, expectedFee, allocatedPaid, statusText, statusColor, isWithdrawnBatch, key }) => (
                    <tr
                      key={key}
                      style={{ borderBottom: '1px solid #f0ece8', transition: 'background 0.2s', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fffafb'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      onClick={() => openModal(student, batch, selectedMonth)}
                    >
                      <td style={{ padding: '20px 24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                          <img src={student.photoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(tField(student.name))}&background=f0ece8&color=111`} alt={tField(student.name)} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} />
                          <div>
                            <div className="dash-list-item-name" style={{ color: 'var(--wine)', fontWeight: 700 }}>{tField(student.name)}</div>
                            <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#aaaaaa', marginTop: 4 }}>{student.studentId}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '20px 24px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>{tField(batch.name)}</span>
                          {isWithdrawnBatch && (
                            <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#fee2e2', color: '#b91c1c', fontWeight: 700 }}>
                              Withdrawn
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '20px 24px' }}><div style={{ fontSize: 16, fontWeight: 800, color: 'var(--black)' }}>HK$ {expectedFee.toLocaleString()}</div></td>
                      <td style={{ padding: '20px 24px' }}>
                        <span className={`badge ${statusColor}`} style={{ borderRadius: 4, padding: '6px 12px', fontSize: 10 }}>{statusText}</span>
                      </td>
                      <td style={{ padding: '20px 24px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          <button
                            className="btn btn-xs btn-outline"
                            style={{ borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 700 }}
                            onClick={e => { e.stopPropagation(); openModal(student, batch, selectedMonth); }}
                          >
                            Log Payment
                          </button>
                          {statusText !== 'PAID IN FULL' && statusText !== 'WITHDRAWN' && (
                            <button
                              className="btn btn-xs btn-primary"
                              style={{ borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 700, background: 'var(--wine)', borderColor: 'var(--wine)', display: 'flex', alignItems: 'center', gap: 4 }}
                              onClick={e => {
                                e.stopPropagation();
                                alertContext.openAlert(student, {
                                  month: selectedMonth.split(' ')[0],
                                  year: selectedMonth.split(' ')[1],
                                  expectedAmount: expectedFee - allocatedPaid,
                                  status: statusText.includes('PARTIAL') ? 'partially paid' : 'pending'
                                });
                              }}
                            >
                              <span>🔔</span> Alert
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>

          {/* ── Mobile Cards ── */}
          <div className="fa-mobile-cards" style={{ padding: 12 }}>
            {(() => {
              const rows = [];
              const filteredStudents = students.filter(s => {
                const inCurrentBatch = s.batchIds?.some(b => (b._id || b.id || b) === selectedBatch) || (s.batch?._id || s.batch) === selectedBatch;
                const inBatchHistory = s.batchHistory?.some(h => (h.batchId?._id || h.batchId)?.toString() === selectedBatch);
                const inRemovedBatches = s.removedBatches?.some(b => (b.batchId?._id || b.batchId || b.batch)?.toString() === selectedBatch);
                const hasPaymentInBatch = payments.some(p =>
                  (p.student?._id === s._id || p.student === s._id || p.studentId === s.studentId) &&
                  ((p.batch?._id || p.batch) === selectedBatch)
                );
                return inCurrentBatch || inBatchHistory || inRemovedBatches || hasPaymentInBatch;
              });

              filteredStudents.forEach(student => {
                let stuBatches = student.batchIds?.length > 0 ? [...student.batchIds] : (student.batch ? [student.batch] : [{ _id: 'unassigned', name: student.currentLevel || 'Unassigned', feeAmount: student.feeAmount || 1800 }]);
                
                const inCurrent = stuBatches.some(b => (b._id || b.id) === selectedBatch);
                if (!inCurrent && selectedBatch && selectedBatch !== 'All') {
                  const bDoc = batches.find(b => b._id === selectedBatch);
                  if (bDoc) {
                    stuBatches.push({ ...bDoc, isWithdrawn: true });
                  }
                }

                const relevantBatches = stuBatches.filter(b => (b._id || b.id) === selectedBatch);

                relevantBatches.forEach((batch, bIndex) => {
                  const { allocatedPaid, expectedFee } = calculateBatchPaidAmount(student, batch, selectedMonth, payments, true);

                  const isWithdrawnBatch = batch.isWithdrawn || student.status === 'Withdrawn';
                  let statusText = 'FEE DUE';
                  let statusColor = 'badge-red';
                  if (allocatedPaid >= expectedFee) { statusText = 'PAID IN FULL'; statusColor = 'badge-green'; }
                  else if (allocatedPaid > 0) { statusText = 'PARTIAL'; statusColor = 'badge-orange'; }
                  else if (isWithdrawnBatch) { statusText = 'WITHDRAWN'; statusColor = 'badge-grey'; }

                  rows.push({ student, batch, expectedFee, allocatedPaid, statusText, statusColor, isWithdrawnBatch, key: `mob-${student._id}-${batch._id || bIndex}` });
                });
              });

              return rows.map(({ student, batch, expectedFee, allocatedPaid, statusText, statusColor, isWithdrawnBatch, key }) => (
                <div
                  key={key}
                  className="fa-student-card"
                  style={{ borderLeft: `4px solid ${statusText === 'PAID IN FULL' ? '#22c55e' : statusText === 'PARTIAL' ? '#f97316' : statusText === 'WITHDRAWN' ? '#9ca3af' : '#e03050'}` }}
                  onClick={() => openModal(student, batch, selectedMonth)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <img
                        src={student.photoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(tField(student.name))}&background=f0ece8&color=111`}
                        alt={tField(student.name)}
                        style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                      />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--wine)' }}>{tField(student.name)}</div>
                        <div style={{ fontSize: 10, fontFamily: 'monospace', color: '#aaa' }}>{student.studentId}</div>
                      </div>
                    </div>
                    <span className={`badge ${statusColor}`} style={{ borderRadius: 4, padding: '5px 10px', fontSize: 10, whiteSpace: 'nowrap', flexShrink: 0 }}>{statusText}</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                    <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 10px' }}>
                      <div className="text-xs text-muted" style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Batch</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#333', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>{tField(batch.name)}</span>
                        {isWithdrawnBatch && (
                          <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: '#fee2e2', color: '#b91c1c', fontWeight: 700 }}>
                            Withdrawn
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 10px' }}>
                      <div style={{ fontSize: 9, textTransform: 'uppercase', color: '#aaa', letterSpacing: 1, fontWeight: 700 }}>Monthly Fee</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--black)', marginTop: 2 }}>HK$ {expectedFee.toLocaleString()}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-xs btn-outline"
                      style={{ flex: 1, borderRadius: 8, padding: '8px', fontSize: 12, fontWeight: 700 }}
                      onClick={e => { e.stopPropagation(); openModal(student, batch, selectedMonth); }}
                    >
                      💰 Log Payment
                    </button>
                    {statusText !== 'PAID IN FULL' && statusText !== 'WITHDRAWN' && (
                      <button
                        className="btn btn-xs btn-primary"
                        style={{ flex: 1, borderRadius: 8, padding: '8px', fontSize: 12, fontWeight: 700, background: 'var(--wine)', borderColor: 'var(--wine)' }}
                        onClick={e => {
                          e.stopPropagation();
                          alertContext.openAlert(student, {
                            month: selectedMonth.split(' ')[0],
                            year: selectedMonth.split(' ')[1],
                            expectedAmount: expectedFee - allocatedPaid,
                            status: statusText === 'PARTIAL' ? 'partially paid' : 'pending'
                          });
                        }}
                      >
                        🔔 Alert
                      </button>
                    )}
                  </div>
                </div>
              ));
            })()}
          </div>

          {!students.length && (
            <div className="dash-empty" style={{ padding: '80px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>👥</div>
              <div className="dash-empty-title">No Students Found</div>
            </div>
          )}
          </>
          )}
        </div>
      )}

      {/* ── Multi-Month Record Payment Modal ───────────────────────────────── */}
      {recordModal && (
        <div className="dash-modal-overlay">
          <div className="dash-modal" style={{ maxWidth: 560, width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="dash-modal-header">
              <div className="dash-modal-title">💰 Record Desk Payment</div>
              <button className="dash-modal-close" onClick={() => setRecordModal(false)}>✕</button>
            </div>
            <div className="dash-modal-body" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Student ID & Batch Info */}
              <div className="form-group" style={{ display: 'flex', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <label className="form-label-style">Student ID *</label>
                  <input
                    className="form-input-style"
                    placeholder="e.g. STU-2026-101"
                    value={recordForm.studentId}
                    onChange={e => setRecordForm(prev => ({ ...prev, studentId: e.target.value }))}
                  />
                  {(() => {
                    const resolvedStudent = students.find(s => s.studentId === recordForm.studentId);
                    if (resolvedStudent) {
                      return (
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#137333', 
                          fontWeight: 700, 
                          marginTop: 6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6
                        }}>
                          <div className="dash-avatar" style={{ width: 32, height: 32, background: '#f0f4f8', color: '#2c3e50' }}>{tField(resolvedStudent.name)[0]}</div>
                          <span style={{ color: '#2c3e50' }}>{tField(resolvedStudent.name)}</span>
                        </div>
                      );
                    } else if (recordForm.studentId) {
                      return (
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#c5221f', 
                          fontWeight: 600, 
                          marginTop: 6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}>
                          <span style={{ background: '#fce8e6', color: '#c5221f', padding: '2px 8px', borderRadius: 4, fontSize: '10px', textTransform: 'uppercase', letterSpacing: 0.5 }}>⚠️ Unresolved</span>
                          <span>Student not found</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                {recordForm._batchName && (
                  <div style={{ flex: 1 }}>
                    <label className="form-label-style">Target Batch</label>
                    <div style={{
                      background: '#faf8f5',
                      border: '1px solid #f0e6da',
                      borderRadius: 8,
                      padding: '10px 14px',
                      color: 'var(--wine)',
                      fontWeight: 700,
                      fontSize: 14,
                      display: 'flex',
                      alignItems: 'center',
                      height: '42px',
                      boxSizing: 'border-box'
                    }}>
                      {recordForm._batchName}
                    </div>
                  </div>
                )}
              </div>

              {/* Month checkboxes */}
              <div className="form-group">
                <label className="form-label-style" style={{ marginBottom: 10, display: 'block' }}>
                  Select Month(s) to Pay *
                  <span style={{ fontWeight: 400, color: '#999', marginLeft: 8, fontSize: 11 }}>
                    HK$ {recordForm._feePerMonth.toLocaleString()} / month
                  </span>
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 8,
                  maxHeight: 260,
                  overflowY: 'auto',
                  padding: 4
                }}>
                  {recordForm._batchMonths.map(month => {
                    const isPaid    = (recordForm._paidMonths || []).includes(month);
                    const checked   = (recordForm.selectedMonths || []).includes(month);
                    const isCurrent = month === CURRENT_MONTH;
                    return (
                      <label
                        key={month}
                        onClick={() => toggleMonth(month)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 12px',
                          borderRadius: 8,
                          border: isPaid
                            ? '2px solid #22c55e'
                            : `2px solid ${checked ? 'var(--wine, #8B1D3B)' : '#e5e5e5'}`,
                          background: isPaid
                            ? 'rgba(34,197,94,0.08)'
                            : checked ? 'rgba(139,29,59,0.06)' : '#fafafa',
                          cursor: isPaid ? 'default' : 'pointer',
                          fontSize: 12,
                          fontWeight: isPaid || checked ? 700 : 400,
                          color: isPaid ? '#16a34a' : checked ? 'var(--wine, #8B1D3B)' : '#444',
                          transition: 'all 0.15s',
                          userSelect: 'none',
                          opacity: isPaid ? 0.85 : 1,
                        }}
                      >
                        {isPaid ? (
                          <span style={{ fontSize: 14, lineHeight: 1 }}>✅</span>
                        ) : (
                          <input
                            type="checkbox"
                            checked={checked}
                            readOnly
                            style={{ accentColor: 'var(--wine, #8B1D3B)', width: 14, height: 14 }}
                          />
                        )}
                        <span>{month}</span>
                        <span style={{
                          fontSize: 9,
                          borderRadius: 4,
                          padding: '1px 5px',
                          marginLeft: 'auto',
                          whiteSpace: 'nowrap',
                          background: isPaid ? '#dcfce7' : isCurrent ? '#f0e6ea' : 'transparent',
                          color: isPaid ? '#16a34a' : 'var(--wine, #8B1D3B)',
                          fontWeight: 700,
                        }}>
                          {isPaid ? 'Paid ✓' : isCurrent ? 'Current' : ''}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Total amount preview */}
              <div style={{
                background: recordForm.selectedMonths.length > 0 ? 'rgba(139,29,59,0.05)' : '#f9f9f9',
                borderRadius: 10,
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: `1px solid ${recordForm.selectedMonths.length > 0 ? 'rgba(139,29,59,0.2)' : '#e5e5e5'}`
              }}>
                <div style={{ fontSize: 13, color: '#666' }}>
                  {recordForm.selectedMonths.length > 0
                    ? `${recordForm.selectedMonths.length} month(s) × HK$ ${recordForm._feePerMonth.toLocaleString()}`
                    : 'No months selected'}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--wine, #8B1D3B)' }}>
                  HK$ {totalAmount.toLocaleString()}
                </div>
              </div>

              {/* Payment Method & Guardian */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: recordForm.paymentMethod === 'Bank/QR Transfer' ? 16 : 0 }}>
                <div className="form-group">
                  <label className="form-label-style">{t('financialAuditor.paymentMethod')}</label>
                  <select
                    className="form-input-style"
                    value={recordForm.paymentMethod}
                    onChange={e => setRecordForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  >
                    <option value="Cash">{t('financialAuditor.methodCash')}</option>
                    <option value="Bank/QR Transfer">Bank / QR Transfer</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label-style">Guardian / Payer Name</label>
                  <select
                    className="form-input-style"
                    value={recordForm.guardianName}
                    onChange={e => setRecordForm(prev => ({ ...prev, guardianName: e.target.value }))}
                  >
                    {recordForm._parentName && <option value={recordForm._parentName}>{recordForm._parentName} (Parent)</option>}
                    <option value="Admin Entry">Admin Entry</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {recordForm.paymentMethod === 'Bank/QR Transfer' && (
                <div style={{ marginTop: '16px' }}>
                  <OrganizationPaymentDetailsWidget containerStyle={{ margin: 0, boxShadow: 'none', border: '1px solid #eaeaea' }} />
                </div>
              )}

              {recordForm.paymentMethod === 'Bank/QR Transfer' && (
                <div className="form-group" style={{ marginTop: 16 }}>
                  <label className="form-label-style">Payment Receipt <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="form-input-style"
                    style={{ padding: '8px' }}
                    onChange={e => setRecordForm(prev => ({ ...prev, receiptFile: e.target.files[0] }))}
                  />
                  {recordForm.receiptFile && (
                    <div style={{ fontSize: 12, color: '#2d7a52', marginTop: 4, fontWeight: 600 }}>
                      ✅ {recordForm.receiptFile.name} selected
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="dash-modal-footer">
              <button className="btn btn-outline" onClick={() => setRecordModal(false)}>{t('financialAuditor.cancel')}</button>
              <button
                className="btn btn-primary"
                onClick={handleRecordSubmit}
                disabled={submitting || recordForm.selectedMonths.length === 0}
                style={{ opacity: (submitting || recordForm.selectedMonths.length === 0) ? 0.6 : 1 }}
              >
                {submitting ? 'Recording...' : `Record Payment — HK$ ${totalAmount.toLocaleString()}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <StudentAlertModal alertContext={alertContext} />
    </Layout>
  );
}
